// apps/smart-shikka-web/src/pages/SupportInbox.tsx
// CORE-HELP-02: staff Support Inbox — views, assign, reply, internal notes, SLA truth.
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../auth.js";
import { useSupportAccess } from "../supportAccess.js";
import { AppNav } from "../components/AppNav.js";
import { useBrandColors } from "@smart-shikka/react";
import {
  supportApi,
  type SupportTicket,
  type SupportTicketDetail,
  type SupportTicketStatus,
  type SupportSlaTruth,
  type SupportChannelTruth,
  type SupportNotification,
} from "../api.js";

const VIEWS = [
  ["new", "नए / खुले"],
  ["human_needed", "मानव सहायता चाहिए"],
  ["assigned_to_me", "मुझे सौंपे गए"],
  ["in_progress", "प्रगति में"],
  ["waiting_customer", "ग्राहक की प्रतीक्षा"],
  ["sla_risk", "SLA जोखिम"],
  ["sla_overdue", "SLA अतिदेय"],
  ["resolved", "हल हुए"],
  ["closed", "बंद"],
] as const;

const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  open: "खुला", human_needed: "मानव सहायता", in_progress: "प्रगति में",
  waiting_customer: "ग्राहक प्रतीक्षा", resolved: "हल हुआ", closed: "बंद",
};

const CATEGORY_LABELS: Record<string, string> = {
  product_help: "प्रोडक्ट सहायता", bug: "बग", deployment_domain: "डिप्लॉय/डोमेन",
  integration_provider: "इंटीग्रेशन", account_access: "अकाउंट", billing_payment: "बिलिंग",
  security_privacy: "सुरक्षा", data_loss: "डेटा लॉस", feature_request: "फ़ीचर", other: "अन्य",
};

const CHANNEL_LABELS: Record<string, string> = {
  in_app: "इन-ऐप", email: "ईमेल", whatsapp: "WhatsApp", telegram: "Telegram",
};
const CHANNEL_STATUS_LABELS: Record<string, string> = {
  ready: "तैयार", blocked_config: "कॉन्फ़िग नहीं",
  adapter_ready_provider_not_configured: "एडाप्टर तैयार — प्रोवाइडर कॉन्फ़िग नहीं",
  post_beta_optional: "बीटा के बाद",
};

function fmtTs(ts: number | string | undefined | null): string {
  if (ts === undefined || ts === null || ts === "") return "—";
  const ms = typeof ts === "number" ? ts : Number(ts);
  const d = Number.isFinite(ms) ? new Date(ms) : new Date(String(ts));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("hi-IN", { day: "numeric", month: "short" }) + " " +
    d.toLocaleTimeString("hi-IN", { hour: "2-digit", minute: "2-digit" });
}

const inputStyle = (C: ReturnType<typeof useBrandColors>): React.CSSProperties => ({
  padding: "7px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
  background: C.bg, fontSize: 12, color: C.fg, outline: "none", fontFamily: "inherit",
});

const btnStyle = (C: ReturnType<typeof useBrandColors>, primary = false): React.CSSProperties => ({
  padding: "6px 12px", borderRadius: 6, border: primary ? "none" : `1px solid ${C.border}`,
  background: primary ? C.accent : "transparent", color: primary ? C.accentFg : C.fg,
  fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
});

function StaffDetail({ token, ticket, onChanged, onBack, isManager }: {
  token: string; ticket: SupportTicket; isManager: boolean;
  onChanged: (t: SupportTicket) => void; onBack: () => void;
}) {
  const C = useBrandColors();
  const [detail, setDetail] = useState<SupportTicketDetail | null>(null);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asNote, setAsNote] = useState(false);

  const load = useCallback(() => {
    supportApi.ticketDetail(token, ticket.ticketId).then(setDetail).catch(() => setError("टिकट लोड नहीं हुआ"));
  }, [token, ticket.ticketId]);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<{ ticket: SupportTicket }>) => {
    if (busy) return;
    setBusy(true); setError(null);
    try { const r = await fn(); onChanged(r.ticket); load(); }
    catch (e) { setError(e instanceof Error ? e.message : "कार्रवाई विफल"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    const text = asNote ? note : reply;
    if (!text.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      if (asNote) await supportApi.internalNote(token, ticket.ticketId, text.trim());
      else await supportApi.reply(token, ticket.ticketId, text.trim());
      setReply(""); setNote(""); load();
    } catch (e) { setError(e instanceof Error ? e.message : "विफल"); }
    finally { setBusy(false); }
  };

  const t = detail?.ticket ?? ticket;

  return (
    <div>
      <button onClick={onBack} style={{ ...btnStyle(C), marginBottom: 12 }}>← इनबॉक्स</button>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.fg, marginBottom: 4 }}>{t.subject}</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
        {t.ticketNumber} · {STATUS_LABELS[t.status]} · {t.sourceApp} · ग्राहक: {t.requesterActorId}
        {t.escalationReason ? ` · कारण: ${t.escalationReason}` : ""}
      </div>
      {error && <div style={{ fontSize: 12, color: C.destructive, marginBottom: 8 }}>{error}</div>}

      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={() => act(() => supportApi.assign(token, t.ticketId))} disabled={busy} style={btnStyle(C)}>मुझे सौंपें</button>
        <button onClick={() => act(() => supportApi.resolve(token, t.ticketId))} disabled={busy} style={btnStyle(C)}>हल हुआ</button>
        <button onClick={() => act(() => supportApi.close(token, t.ticketId))} disabled={busy} style={btnStyle(C)}>बंद करें</button>
        <button onClick={() => act(() => supportApi.reopen(token, t.ticketId))} disabled={busy} style={btnStyle(C)}>दोबारा खोलें</button>
        <select value={t.category} onChange={(e) => act(() => supportApi.changeCategory(token, t.ticketId, e.target.value))} style={inputStyle(C)}>
          {Object.entries(CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={t.priority} onChange={(e) => act(() => supportApi.changePriority(token, t.ticketId, e.target.value))} style={inputStyle(C)}>
          {(["low", "normal", "high", "urgent"] as const).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Full thread incl. internal notes (staff-only) + events timeline */}
      <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>
        {(detail?.messages ?? []).map((m) => (
          <div key={m.messageId} style={{
            border: `1px solid ${m.kind === "internal_note" ? C.warning : C.border}`,
            borderRadius: 8, padding: "8px 12px",
            background: m.kind === "internal_note" ? C.warningBg : m.kind === "human_reply" ? C.accent : C.card,
            marginLeft: m.kind === "customer_message" ? 0 : 20,
            marginRight: m.kind === "customer_message" ? 20 : 0,
          }}>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>
              {m.kind === "internal_note" ? "🔒 आंतरिक नोट (ग्राहक को नहीं दिखता)" : m.kind === "human_reply" ? "सपोर्ट" : m.kind === "system_event" ? "सिस्टम" : "ग्राहक"} · {fmtTs(m.createdAt)}
            </div>
            <div style={{ fontSize: 12, color: C.fg, whiteSpace: "pre-wrap" }}>{m.body}</div>
          </div>
        ))}
      </div>

      {(detail?.events ?? []).length > 0 && (
        <div style={{ marginBottom: 14, padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: 8, background: C.panel }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 6 }}>समय-रेखा</div>
          {(detail?.events ?? []).map((e) => (
            <div key={e.eventId} style={{ fontSize: 10, color: C.muted }}>
              {fmtTs(e.createdAt)} — {e.eventType} ({e.actorId.slice(0, 24)})
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gap: 8 }}>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: C.fg }}>
          <input type="checkbox" checked={asNote} onChange={(e) => setAsNote(e.target.checked)} />
          आंतरिक नोट के रूप में (ग्राहक को नहीं दिखेगा)
        </label>
        <textarea value={asNote ? note : reply} onChange={(e) => (asNote ? setNote : setReply)(e.target.value)}
          placeholder={asNote ? "आंतरिक नोट..." : "ग्राहक को उत्तर..."} rows={3}
          style={{ ...inputStyle(C), width: "100%", resize: "vertical", boxSizing: "border-box" }} maxLength={16000} />
        <div><button onClick={send} disabled={busy || !(asNote ? note : reply).trim()} style={btnStyle(C, true)}>
          {asNote ? "नोट जोड़ें" : "उत्तर भेजें"}
        </button></div>
      </div>
      {isManager && null}
    </div>
  );
}

export function SupportInboxPage() {
  const { token } = useAuth();
  const { access, loaded: accessLoaded } = useSupportAccess();
  const C = useBrandColors();
  const [view, setView] = useState<string>("new");
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selected, setSelected] = useState<SupportTicket | null>(null);
  const [sla, setSla] = useState<SupportSlaTruth | null>(null);
  const [channels, setChannels] = useState<SupportChannelTruth[]>([]);
  const [notifs, setNotifs] = useState<SupportNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [workerResult, setWorkerResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    if (!token) { setLoading(false); return; }
    supportApi.inbox(token, view).then((r) => setTickets(r.tickets)).catch(() => setTickets([])).finally(() => setLoading(false));
    supportApi.sla(token).then((r) => setSla(r.sla)).catch(() => {});
    supportApi.notifications(token)
      .then((r) => { setChannels(r.channels); setNotifs(r.notifications); setUnread(r.unread); })
      .catch(() => {});
  }, [token, view]);

  useEffect(refresh, [refresh]);

  // Deep link: #/support?ticket=<id> opens that ticket detail directly (§6).
  useEffect(() => {
    const q = window.location.hash.split("?")[1];
    const ticketId = new URLSearchParams(q ?? "").get("ticket");
    if (!ticketId || !token || selected) return;
    supportApi.ticketDetail(token, ticketId)
      .then((d) => setSelected(d.ticket))
      .catch(() => {});
  }, [token, selected]);

  const openNotification = async (n: SupportNotification) => {
    if (!token) return;
    if (n.readAt === undefined) {
      await supportApi.markNotificationsRead(token, [n.notificationId]).catch(() => undefined);
      setUnread((u) => Math.max(0, u - 1));
      setNotifs((prev) => prev.map((x) => (x.notificationId === n.notificationId ? { ...x, readAt: Date.now() } : x)));
    }
    supportApi.ticketDetail(token, n.ticketId).then((d) => setSelected(d.ticket)).catch(() => {});
  };

  const runWorker = async () => {
    if (!token) return;
    try {
      const r = await supportApi.processNotifications(token);
      setWorkerResult(`${r.processed} संसाधित · ${r.delivered} डिलीवर · ${r.retried} retry · ${r.failed} विफल`);
      setChannels(r.channels);
    } catch (e) {
      setWorkerResult(e instanceof Error ? e.message : "worker विफल");
    }
  };

  // Gate on the server-resolved capability, never the login role (merge-gate
  // §1): fail-closed to the deny screen while the truth loads, same deny when
  // the account has no support grant regardless of its privileged role.
  if (accessLoaded && !access.canUseInbox) {
    return (
      <div style={{ display: "flex", height: "100%", background: C.bg, fontFamily: C.fontSans }}>
        <AppNav activeRoute="support" />
        <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: 13, color: C.muted }}>यह सतह केवल सपोर्ट टीम के लिए है।</div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100%", background: C.bg, fontFamily: C.fontSans }}>
      <AppNav activeRoute="support" />
      <aside style={{ width: 200, background: C.panel, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", padding: "12px 8px", gap: 2, flexShrink: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, padding: "0 8px 8px" }}>सपोर्ट इनबॉक्स</div>
        {VIEWS.map(([id, label]) => (
          <button key={id} onClick={() => { setView(id); setSelected(null); }} style={{
            textAlign: "left", padding: "7px 10px", borderRadius: 6, border: "none",
            background: view === id ? C.accent : "transparent", color: view === id ? C.accentFg : C.fg,
            fontSize: 12, cursor: "pointer", fontFamily: "inherit",
          }}>{label}</button>
        ))}
      </aside>
      <main style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
        <div style={{ maxWidth: 820 }}>
          {selected ? (
            <StaffDetail token={token!} ticket={selected} isManager={access.canManageSla}
              onChanged={(t) => { setSelected(t); refresh(); }} onBack={() => { setSelected(null); refresh(); }} />
          ) : (
            <>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: C.fg, margin: "0 0 16px" }}>
                {VIEWS.find(([id]) => id === view)?.[1]}
              </h1>

              {unread > 0 && (
                <div style={{ marginBottom: 16, border: `1px solid ${C.border}`, borderRadius: 8, background: C.card, overflow: "hidden" }}>
                  <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 700, color: C.accentFg, borderBottom: `1px solid ${C.border}` }}>
                    {unread} नई सूचनाएँ
                  </div>
                  {notifs.filter((n) => n.readAt === undefined).slice(0, 5).map((n) => (
                    <div key={n.notificationId} onClick={() => void openNotification(n)} style={{
                      padding: "8px 14px", fontSize: 11, color: C.fg, cursor: "pointer",
                      borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between",
                    }}>
                      <span>{typeof n.payload?.subject === "string" ? n.payload.subject : n.eventType}</span>
                      <span style={{ color: C.muted }}>{fmtTs(n.createdAt)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Delivery truth — channels + worker */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: C.card, padding: "10px 14px", fontSize: 11 }}>
                  <div style={{ fontWeight: 700, color: C.fg, marginBottom: 4 }}>नोटिफ़िकेशन चैनल</div>
                  {channels.map((ch) => (
                    <div key={ch.channel} style={{ color: C.muted }}>
                      {CHANNEL_LABELS[ch.channel] ?? ch.channel}: {CHANNEL_STATUS_LABELS[ch.status] ?? ch.status}
                    </div>
                  ))}
                </div>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: C.card, padding: "10px 14px", fontSize: 11 }}>
                  <div style={{ fontWeight: 700, color: C.fg, marginBottom: 4 }}>SLA</div>
                  <div style={{ color: sla?.configured ? C.successFg : C.warning }}>
                    {sla?.configured ? `${sla.policies.length} नीतियां सक्रिय` : "NOT_CONFIGURED"}
                  </div>
                </div>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: C.card, padding: "10px 14px", fontSize: 11 }}>
                  <div style={{ fontWeight: 700, color: C.fg, marginBottom: 4 }}>आउटबॉक्स वर्कर</div>
                  <button onClick={runWorker} style={btnStyle(C)}>डिलीवरी चलाएँ</button>
                  {workerResult && <div style={{ color: C.muted, marginTop: 4 }}>{workerResult}</div>}
                </div>
              </div>

              <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", background: C.card }}>
                {loading && <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: C.muted }}>लोड हो रहे हैं...</div>}
                {!loading && tickets.length === 0 && (
                  <div style={{ padding: 32, textAlign: "center", fontSize: 12, color: C.muted }}>इस दृश्य में कोई टिकट नहीं।</div>
                )}
                {tickets.map((t, i) => (
                  <div key={t.ticketId} onClick={() => setSelected(t)} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", cursor: "pointer",
                    borderBottom: i < tickets.length - 1 ? `1px solid ${C.border}` : "none",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>
                        {t.ticketNumber} · {CATEGORY_LABELS[t.category] ?? t.category} · {t.priority} · {t.sourceApp} · {fmtTs(t.updatedAt)}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 600, background: C.panel, color: C.muted }}>
                      {STATUS_LABELS[t.status]}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
